import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import type { BotAvatarSet, ThreadMessage } from "@rakazo/contracts";
import { BotAvatar, cn } from "@rakazo/ui-web";
import { Clock3, Radio, UserRound } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { FALLBACK_BOT_COLOR } from "./chat-shared";

/** Author and local time stay with the message, including files and approval cards. */
export function ChatMessageFrame({
  message,
  plain,
  user,
  bot,
  botId,
  botName,
  children,
}: {
  message: ThreadMessage;
  plain: boolean;
  user?: { name?: string | null; image?: string | null };
  bot?: { name?: string; color: string; avatarSet?: BotAvatarSet };
  botId?: string;
  botName?: string;
  children: ReactNode;
}) {
  const { t } = useLingui();
  const [failedImage, setFailedImage] = useState<string | null>(null);
  if (plain) return children;
  const trigger = message.role === "user" ? message.routineTrigger : undefined;
  const outgoing = message.role === "user" && !trigger;
  const name = outgoing
    ? user?.name?.trim() || t`You`
    : trigger === "webhook"
      ? t`Webhook`
      : trigger === "routine"
        ? t`Routine`
        : bot?.name || botName || t`Bot`;
  const initials = user?.name
    ?.trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => Array.from(part)[0])
    .join("")
    .toLocaleUpperCase();
  const date = new Date(message.createdAt);
  const validDate = Number.isFinite(date.getTime());
  const locale = i18n.locale || undefined;
  const TriggerIcon = trigger === "webhook" ? Radio : Clock3;
  return (
    <div
      data-testid="chat-message-frame"
      data-message-side={outgoing ? "outgoing" : "incoming"}
      className={cn(
        "wurkees-message-frame flex w-full min-w-0 items-start gap-2.5 sm:gap-3",
        outgoing && "flex-row-reverse",
      )}
    >
      <div
        aria-hidden="true"
        className="mt-0.5 flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-base font-medium text-muted-foreground sm:size-10"
      >
        {outgoing ? (
          user?.image && failedImage !== user.image ? (
            <img
              src={user.image}
              alt=""
              className="size-full object-cover"
              onError={() => setFailedImage(user.image ?? null)}
            />
          ) : initials ? (
            <span>{initials}</span>
          ) : (
            <UserRound className="size-5" />
          )
        ) : trigger ? (
          <TriggerIcon className="size-5" />
        ) : (
          <BotAvatar
            avatarSet={bot?.avatarSet}
            color={bot?.color ?? FALLBACK_BOT_COLOR}
            identity={botId}
            size={36}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-base leading-5",
            outgoing && "justify-end",
          )}
        >
          <span dir="auto" className="min-w-0 break-words font-medium text-foreground">
            {name}
          </span>
          {validDate ? (
            <time
              dateTime={message.createdAt}
              title={date.toLocaleString(locale)}
              className="shrink-0 text-base font-normal text-muted-foreground"
            >
              {date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}
            </time>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}
